import type { SalesChecklistItem } from "@expert/contentful";
import type { JSX } from "react";

import { Flex, Image, Text } from "@mantine/core";

import type { OfferChecklistState } from "../../../../../stores/useOfferChecklistStore";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { useOfferChecklistStore } from "../../../../../stores/useOfferChecklistStore";
import { UncheckedRadioIcon } from "../../Icons";
import { GreenCheckMark } from "../../Icons/GreenCheckMark";
import { RichPhrase } from "../../RichPhrase";
import { hasRenderableText } from "../utils/hasRenderableText";
import { BillingDetailsToolTip } from "./BillingDetailsToolTip";

export function ChecklistItem({
    item,
    productSku,
}: {
    item: SalesChecklistItem;
    productSku: string;
}): JSX.Element | null {
    const {
        phrase,
        richPhrase,
        subheading,
        subfooter,
        highlightColor,
        key,
        popupDescriptionText,
        popupLinkText,
        icon,
    } = item;
    const { sessionId } = useSalesContext();
    const data = useOfferChecklistStore((state) => state.getData(sessionId, productSku));

    // Render nothing when the item has no text to show — e.g. a cohort override
    // that blanks the phrase to drop the bullet while keeping its example carousel
    // (rendered separately by ChecklistSection). Hooks above run unconditionally.
    // An override that blanks `phrase` must blank `richPhrase` too, or the bullet survives.
    if (!hasRenderableText(item)) return null;

    const isDetected = key in data ? data[key as keyof OfferChecklistState["data"]] : false;
    // A richPhrase row highlights the say-verbatim words themselves (mark.verbatim), so the row-level
    // markers for the same thing come off: the red wash (its padding with it — that only existed to
    // give the tinted block breathing room) and the asterisk. Items without a richPhrase keep both,
    // so e.g. BILLING still reads as required.
    const rowHighlight = richPhrase ? undefined : highlightColor;
    const rowIcon = richPhrase ? undefined : icon;

    return (
        <Flex direction="column">
            <Text mb="3xs" c="dark.1" size="xs">
                {subheading}
            </Text>
            <Flex p={rowHighlight ? "10px 10px 10px 4px" : undefined} bg={isDetected ? undefined : rowHighlight}>
                {isDetected ? <GreenCheckMark /> : <UncheckedRadioIcon />}
                <Text
                    // richPhrase can contain a nested list, which is invalid inside Mantine's
                    // default <p>; render a <div> in that case.
                    component={richPhrase ? "div" : "p"}
                    w="100%"
                    ml="3xs"
                    mr="3xs"
                    c={isDetected ? "dark.3" : "dark.1"}
                    size="xs"
                >
                    {richPhrase ? <RichPhrase markdown={richPhrase} /> : phrase}
                </Text>
                {rowIcon && !isDetected && (
                    <Image w="auto" style={{ alignSelf: "flex-start" }} fit="contain" src={rowIcon} />
                )}
            </Flex>
            {subfooter && (
                <Text mt="3xs" c="dark.1" size="xs">
                    {subfooter}
                </Text>
            )}
            {popupLinkText && popupDescriptionText && (
                <BillingDetailsToolTip title={popupLinkText} body={popupDescriptionText} />
            )}
        </Flex>
    );
}
